'use client'

import { useState, useEffect, useCallback } from 'react'
import { User } from '@/types'
import { CheckCircle, XCircle, Clock, AlertCircle, Eye, Download, X } from 'lucide-react'
import { getApiUrl } from '@/lib/apiUrl'

interface KycData {
  personal_info: {
    first_name: string
    last_name: string
    phone: string
    address: string
    city: string
    state: string
    postal_code: string
    date_of_birth: string
    nationality: string
    occupation: string
    employer: string
    monthly_income: number
  }
  documents: {
    id_card: {
      file_url: string
      file_name: string
      status: 'pending' | 'approved' | 'rejected'
      notes?: string
    }
    proof_of_address: {
      file_url: string
      file_name: string
      status: 'pending' | 'approved' | 'rejected'
      notes?: string
    }
    proof_of_income: {
      file_url: string
      file_name: string
      status: 'pending' | 'approved' | 'rejected'
      notes?: string
    }
    bank_statement: {
      file_url: string
      file_name: string
      status: 'pending' | 'approved' | 'rejected'
      notes?: string
    }
  }
  verification_status: 'not_applied' | 'pending' | 'in_review' | 'approved' | 'rejected'
  submitted_at?: string
  reviewed_at?: string
  admin_notes?: string
}

interface UserWithKyc extends Omit<User, 'kyc_data'> {
  kyc_data?: KycData
}

interface KycVerificationWorkflowProps {
  onUpdate?: () => void
}

export default function KycVerificationWorkflow({ onUpdate }: KycVerificationWorkflowProps) {
  const [users, setUsers] = useState<UserWithKyc[]>([])
  const [loading, setLoading] = useState(true)
  // const [data, setData] = useState<any[]>([])
  // const [error, setError] = useState<any>(null)
  const [selectedUser, setSelectedUser] = useState<UserWithKyc | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [filter, setFilter] = useState<'all' | 'not_applied' | 'pending' | 'in_review' | 'approved' | 'rejected'>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [message, setMessage] = useState('')
  const [processing, setProcessing] = useState<string | null>(null)

  // Load users with KYC data
  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      // Get auth token
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('Please log in to view KYC verifications')
      }

      // Fetch all users from admin endpoint
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/users?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to load users: ${response.statusText}`)
      }

      const responseData = await response.json()
      
      // Filter users with KYC documents or KYC status (landlords and clients, not admins)
      // Include all non-admin users so admins can see who needs KYC
      const usersWithKyc = (responseData.users || []).filter((user: any) => {
        // Show all non-admin users (those who need KYC, have applied, or have been reviewed)
        return user.role !== 'admin'
      })

      // Transform users to match frontend format
      const transformedUsers = usersWithKyc.map((user: any) => ({
        ...user,
        id: user._id || user.id,
        kyc_data: {
          personal_info: {
            first_name: user.firstName,
            last_name: user.lastName,
            phone: user.phone,
            address: user.profile?.address?.street || '',
            city: user.profile?.address?.city || '',
            state: user.profile?.address?.state || '',
            postal_code: user.profile?.address?.zipCode || '',
            date_of_birth: user.profile?.dateOfBirth || '',
            nationality: '',
            occupation: '',
            employer: '',
            monthly_income: 0
          },
          documents: user.kyc?.documents?.reduce((acc: any, doc: any) => {
            // Map backend document types to frontend format
            const docTypeMap: Record<string, string> = {
              'id': 'id_card',
              'proof_of_address': 'proof_of_address',
              'income_proof': 'proof_of_income'
            }
            const frontendType = docTypeMap[doc.type] || doc.type
            acc[frontendType] = {
              file_url: doc.url,
              file_name: doc.url.split('/').pop() || 'document',
              status: doc.status || 'pending',
              notes: ''
            }
            return acc
          }, {}) || {},
          verification_status: user.kyc?.status === 'verified' ? 'approved' : 
                              user.kyc?.status === 'rejected' ? 'rejected' : 
                              user.kyc?.status === 'pending' ? 'pending' :
                              (user.kyc?.status === null || user.kyc?.status === undefined) ? 'not_applied' : 'not_applied',
          submitted_at: user.kyc?.documents?.[0]?.uploadedAt || user.createdAt,
          reviewed_at: user.kyc?.verifiedAt,
          admin_notes: user.kyc?.rejectedReason
        }
      }))

      setUsers(transformedUsers)
    } catch (error) {
      console.error('Error loading users:', error)
      setMessage(`Failed to load users: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  // Filter users based on current filter and search
  const filteredUsers = users.filter(user => {
    let matchesFilter = false
    if (filter === 'all') {
      matchesFilter = true
    } else if (filter === 'not_applied') {
      const status = user.kyc_data?.verification_status || user.kyc?.status
      matchesFilter = !status || status === 'not_applied' || status === null || status === undefined
    } else {
      // For other statuses, check both kyc_data and kyc.status
      const status = user.kyc_data?.verification_status || user.kyc?.status
      if (filter === 'approved') {
        matchesFilter = status === 'approved' || status === 'verified'
      } else {
        matchesFilter = status === filter
      }
    }
    
    const matchesSearch = !searchTerm || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.kyc_data?.personal_info?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.kyc_data?.personal_info?.last_name?.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesFilter && matchesSearch
  })

  // Get verification status stats
  const stats = {
    total: users.length,
    not_applied: users.filter(u => {
      const status = u.kyc_data?.verification_status || u.kyc?.status
      return !status || status === 'not_applied' || status === null || status === undefined
    }).length,
    pending: users.filter(u => u.kyc_data?.verification_status === 'pending' || u.kyc?.status === 'pending').length,
    in_review: users.filter(u => u.kyc_data?.verification_status === 'in_review').length,
    approved: users.filter(u => u.kyc_data?.verification_status === 'approved' || u.kyc?.status === 'verified').length,
    rejected: users.filter(u => u.kyc_data?.verification_status === 'rejected' || u.kyc?.status === 'rejected').length
  }

  // Update user verification status
  const updateVerificationStatus = async (userId: string, status: 'in_review' | 'approved' | 'rejected', notes?: string) => {
    setProcessing(userId)
    setMessage('')

    try {
      // Get auth token
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('Please log in to update verification status')
      }

      const apiUrl = getApiUrl()
      
      // Map frontend status to backend status
      const backendStatus = status === 'approved' ? 'verified' : 
                           status === 'rejected' ? 'rejected' : 
                           'pending'

      // Call backend API to update KYC status
      const response = await fetch(`${apiUrl}/users/admin/${userId}/kyc`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: backendStatus,
          rejectedReason: status === 'rejected' ? notes : undefined
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `Failed to update status: ${response.statusText}`)
      }

      await response.json() // Response data not used

      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? {
              ...user,
              kyc_data: {
                ...user.kyc_data!,
                verification_status: status,
                reviewed_at: new Date().toISOString(),
                admin_notes: notes
              },
              is_verified: status === 'approved'
            }
          : user
      ))

      setMessage(`User verification status updated to ${status}`)
      
      // Reload users to get updated data
      await loadUsers()
      
      if (onUpdate) {
        onUpdate()
      }
    } catch (error) {
      console.error('Update error:', error)
      setMessage(`Failed to update status: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setProcessing(null)
    }
  }

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>
      case 'in_review':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Eye className="w-3 h-3 mr-1" />
          In Review
        </span>
      case 'approved':
      case 'verified':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Approved
        </span>
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Rejected
        </span>
      case 'not_applied':
      case null:
      case undefined:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          KYC Needed
        </span>
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          KYC Needed
        </span>
    }
  }

  // Get document status badge
  const getDocumentStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Pending
        </span>
      case 'approved':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Approved
        </span>
      case 'rejected':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Rejected
        </span>
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Unknown
        </span>
    }
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading KYC verifications...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">KYC Verification Workflow</h2>
          <p className="text-gray-600">Review and manage user identity verification requests</p>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.includes('updated') 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">KYC Needed</p>
              <p className="text-2xl font-bold text-red-600">{stats.not_applied}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Eye className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">In Review</p>
              <p className="text-2xl font-bold text-blue-600">{stats.in_review}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Approved</p>
              <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="not_applied">KYC Needed</option>
              <option value="pending">Pending</option>
              <option value="in_review">In Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Verification Requests ({filteredUsers.length})
          </h3>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p>No verification requests found matching your criteria.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <div key={user.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {user.kyc_data?.personal_info?.first_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {user.kyc_data?.personal_info?.first_name && user.kyc_data?.personal_info?.last_name
                            ? `${user.kyc_data.personal_info.first_name} ${user.kyc_data.personal_info.last_name}`
                            : user.email
                          }
                        </h4>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <p className="text-xs text-gray-400">
                          Role: {user.role} • Submitted: {user.kyc_data?.submitted_at ? formatDate(user.kyc_data.submitted_at) : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {getStatusBadge(user.kyc_data?.verification_status || user.kyc?.status || 'not_applied')}
                    
                    <button
                      onClick={() => {
                        setSelectedUser(user)
                        setShowDetails(true)
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Review
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KYC Details Modal */}
      {showDetails && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  KYC Verification Details
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Personal Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Full Name:</span>
                    <span className="ml-2 text-gray-900">
                      {selectedUser.kyc_data?.personal_info?.first_name} {selectedUser.kyc_data?.personal_info?.last_name}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Phone:</span>
                    <span className="ml-2 text-gray-900">
                      {selectedUser.phone || selectedUser.kyc_data?.personal_info?.phone || (selectedUser.kyc_data as any)?.phone || 'Not provided'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Date of Birth:</span>
                    <span className="ml-2 text-gray-900">
                      {selectedUser.profile?.dateOfBirth 
                        ? new Date(selectedUser.profile.dateOfBirth).toLocaleDateString()
                        : (selectedUser.kyc_data as any)?.dateOfBirth 
                        ? ((selectedUser.kyc_data as any).dateOfBirth.includes('T') 
                            ? new Date((selectedUser.kyc_data as any).dateOfBirth).toLocaleDateString()
                            : (selectedUser.kyc_data as any).dateOfBirth)
                        : selectedUser.kyc_data?.personal_info?.date_of_birth || 'Not provided'
                      }
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Nationality:</span>
                    <span className="ml-2 text-gray-900">{selectedUser.kyc_data?.personal_info?.nationality}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Occupation:</span>
                    <span className="ml-2 text-gray-900">{selectedUser.kyc_data?.personal_info?.occupation}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Employer:</span>
                    <span className="ml-2 text-gray-900">{selectedUser.kyc_data?.personal_info?.employer}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Monthly Income:</span>
                    <span className="ml-2 text-gray-900">
                      {selectedUser.kyc_data?.personal_info?.monthly_income 
                        ? formatCurrency(selectedUser.kyc_data.personal_info.monthly_income)
                        : 'N/A'
                      }
                    </span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-medium text-gray-700">Address:</span>
                    <span className="ml-2 text-gray-900">
                      {selectedUser.profile?.address 
                        ? `${selectedUser.profile.address.street || ''}, ${selectedUser.profile.address.city || ''}, ${selectedUser.profile.address.state || ''}`.trim().replace(/^,\s*|,\s*$/g, '') || 'Not provided'
                        : (selectedUser.kyc_data as any)?.address 
                        ? (selectedUser.kyc_data as any).address
                        : selectedUser.kyc_data?.personal_info?.address 
                        ? `${selectedUser.kyc_data.personal_info.address || ''}, ${selectedUser.kyc_data.personal_info.city || ''}, ${selectedUser.kyc_data.personal_info.state || ''}`.trim().replace(/^,\s*|,\s*$/g, '') || 'Not provided'
                        : 'Not provided'
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(selectedUser.kyc_data?.documents || {}).map(([docType, doc]) => (
                    <div key={docType} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-gray-900 capitalize">
                          {docType.replace('_', ' ')}
                        </h5>
                        {getDocumentStatusBadge(doc.status)}
                      </div>
                      
                      {doc.file_url ? (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600">{doc.file_name}</p>
                          <div className="flex space-x-2">
                            <a
                              href={doc.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </a>
                            <a
                              href={doc.file_url}
                              download={doc.file_name}
                              className="inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Download
                            </a>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No document uploaded</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Admin Actions */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Verification Decision</h4>
                
                {selectedUser.kyc_data?.verification_status === 'pending' && (
                  <div className="flex space-x-3">
                    <button
                      onClick={() => updateVerificationStatus(selectedUser.id, 'in_review')}
                      disabled={processing === selectedUser.id}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {processing === selectedUser.id ? 'Processing...' : 'Mark for Review'}
                    </button>
                    <button
                      onClick={() => updateVerificationStatus(selectedUser.id, 'approved')}
                      disabled={processing === selectedUser.id}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {processing === selectedUser.id ? 'Processing...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => updateVerificationStatus(selectedUser.id, 'rejected')}
                      disabled={processing === selectedUser.id}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      {processing === selectedUser.id ? 'Processing...' : 'Reject'}
                    </button>
                  </div>
                )}

                {selectedUser.kyc_data?.verification_status === 'in_review' && (
                  <div className="flex space-x-3">
                    <button
                      onClick={() => updateVerificationStatus(selectedUser.id, 'approved')}
                      disabled={processing === selectedUser.id}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {processing === selectedUser.id ? 'Processing...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => updateVerificationStatus(selectedUser.id, 'rejected')}
                      disabled={processing === selectedUser.id}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      {processing === selectedUser.id ? 'Processing...' : 'Reject'}
                    </button>
                  </div>
                )}

                {(selectedUser.kyc_data?.verification_status === 'approved' || 
                  selectedUser.kyc_data?.verification_status === 'rejected') && (
                  <div className="text-sm text-gray-600">
                    <p>Status: {selectedUser.kyc_data.verification_status}</p>
                    <p>Reviewed: {selectedUser.kyc_data.reviewed_at ? formatDate(selectedUser.kyc_data.reviewed_at) : 'N/A'}</p>
                    {selectedUser.kyc_data.admin_notes && (
                      <p>Notes: {selectedUser.kyc_data.admin_notes}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


